import { DashboardRouteParams } from '@/types';
import { smartDecodeURIComponent } from '@/utils/uri-utils';
import { useParams } from 'react-router-dom';

type PropertyDeckardIdHookResult = {
  /**
   * The encoded property deckardId from the URL params
   */
  encodedPropertyDeckardId: string;
  /**
   * The unencoded property deckardId from the URL params
   */
  propertyDeckardId: string;
};

type OptionalPropertyDeckardIdHookResult = {
  /**
   * The encoded property deckardId from the URL params
   */
  encodedPropertyDeckardId: string | null;
  /**
   * The unencoded property deckardId from the URL params
   */
  propertyDeckardId: string | null | undefined;
};

/**
 * Hook that returns the encoded and unencoded deckardId from URL params
 * @returns Object containing encoded and unencoded deckardId
 * @throws Error if deckardId is not present in URL
 */
export const usePropertyDeckardId = (): PropertyDeckardIdHookResult => {
  const { propertyDeckardId: encodedPropertyDeckardId } =
    useParams<DashboardRouteParams>();

  if (!encodedPropertyDeckardId) {
    throw new Error(
      `propertyDeckardId not found in URL params. Use useOptionalPropertyDeckardId hook if propertyDeckardId may be absent.`
    );
  }

  const propertyDeckardId = smartDecodeURIComponent(encodedPropertyDeckardId);

  return { encodedPropertyDeckardId, propertyDeckardId };
};

/**
 * Hook that returns the encoded and unencoded deckardId from URL params or null if not present
 * @returns Object containing encoded and unencoded deckardId, or null if not found
 */
export const useOptionalPropertyDeckardId =
  (): OptionalPropertyDeckardIdHookResult => {
    const { propertyDeckardId: encodedPropertyDeckardId } =
      useParams<DashboardRouteParams>();

    if (!encodedPropertyDeckardId) {
      return {
        encodedPropertyDeckardId: null,
        propertyDeckardId: null,
      };
    }

    const propertyDeckardId = smartDecodeURIComponent(encodedPropertyDeckardId);

    return { encodedPropertyDeckardId, propertyDeckardId };
  };
